import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { 
  Search, 
  AlertTriangle, 
  Image, 
  Activity,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';

// Module-level flags to prevent multiple initializations across component instances
// NEVER reset these during the session
let dashboardInitialized = false;
let dashboardInitInProgress = false;
let dashboardApiCallsMade = false; // Track if API calls have been made (never reset)

const Dashboard = ({ searchStatus, showNotification }) => {
  const [stats, setStats] = useState({
    stolenImages: 0,
    totalMatches: 0,
    recentMatches: [],
    systemStatus: 'healthy'
  });
  const [loading, setLoading] = useState(true);
  const initRef = useRef(false);
  const initCompleteRef = useRef(false);

  // Wrap API function in useCallback to prevent recreation on every render
  const fetchDashboardData = useCallback(async () => {
    try {
      console.log('[Dashboard] Calling fetchDashboardData...');
      setLoading(true);
      
      // Make API calls - fetch fresh data when Dashboard opens
      const stolenResponse = await axios.get('https://outiloeuvresvolees.inouiagency.com/api/stolen-images');
      const matchesResponse = await axios.get('https://outiloeuvresvolees.inouiagency.com/api/matches');
      const healthResponse = await axios.get('https://outiloeuvresvolees.inouiagency.com/api/health');
      
      setStats({
        stolenImages: stolenResponse.data.count || 0,
        totalMatches: matchesResponse.data.count || 0,
        recentMatches: (matchesResponse.data.matches || []).slice(0, 5),
        systemStatus: healthResponse.data.status === 'healthy' ? 'healthy' : 'error'
      });
      
      console.log('[Dashboard] Fetched dashboard data:', {
        stolenImages: stolenResponse.data.count || 0,
        totalMatches: matchesResponse.data.count || 0,
        recentMatches: (matchesResponse.data.matches || []).slice(0, 5).length
      });
      
    } catch (error) {
      console.error('[Dashboard] Error fetching dashboard data:', error);
      showNotification('Error loading dashboard data', 'error');
      setStats(prevStats => ({ ...prevStats, systemStatus: 'error' }));
    } finally {
      setLoading(false);
      console.log('[Dashboard] Loading set to false');
    }
  }, [showNotification]);

  useEffect(() => {
    // Always fetch data when Dashboard component mounts
    // Reset init flags to allow fresh initialization on each mount
    initRef.current = false;
    initCompleteRef.current = false;
    
    // If another instance is initializing, wait a bit and try again
    if (dashboardInitInProgress) {
      console.log('[Dashboard] Initialization in progress, will retry...');
      const timeout = setTimeout(() => {
        if (!dashboardInitInProgress) {
          fetchDashboardData();
        }
      }, 100);
      return () => clearTimeout(timeout);
    }
    
    dashboardInitialized = true;
    dashboardInitInProgress = true;
    dashboardApiCallsMade = true; // Mark as done - NEVER reset this
    initRef.current = true;
    initCompleteRef.current = true;
    
    console.log('[Dashboard] Initializing and fetching data...');
    fetchDashboardData().finally(() => {
      dashboardInitInProgress = false;
    });
    
    // Reset refs on unmount so next mount can initialize
    return () => {
      initRef.current = false;
      initCompleteRef.current = false;
    };
    // Empty deps array is intentional - we want this to run on every mount
    // Guards prevent re-execution within the same mount
  }, []);

  // Refresh dashboard when page becomes visible (no polling)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && initRef.current && dashboardApiCallsMade) {
        console.log('[Dashboard] Page became visible - refreshing dashboard data');
        fetchDashboardData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchDashboardData]);

  const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className={`text-3xl font-bold ${color}`}>{value}</p>
          {subtitle && (
            <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`p-3 rounded-full ${color.replace('text-', 'bg-').replace('600', '100')}`}>
          <Icon className={`h-6 w-6 ${color}`} />
        </div>
      </div>
    </div>
  );

  const SearchStatusCard = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Search Status</h3>
        <div className={`flex items-center space-x-2 ${
          searchStatus.is_running ? 'text-blue-600' : 'text-gray-500'
        }`}>
          <Activity className={`h-4 w-4 ${searchStatus.is_running ? 'animate-pulse' : ''}`} />
          <span className="text-sm font-medium">
            {searchStatus.is_running ? 'Running' : 'Idle'}
          </span>
        </div>
      </div>
      
      {searchStatus.is_running ? (
        <div className="space-y-3">
          <div>
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Searching: {searchStatus.current_search}</span>
              <span>{searchStatus.progress.toFixed(0)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${searchStatus.progress}%` }}
              ></div>
            </div>
          </div>
          <div className="flex justify-between text-sm text-gray-600">
            <span>Images processed: {searchStatus.total_images_processed}</span>
            <span>Matches found: {searchStatus.matches_found}</span>
          </div>
        </div>
      ) : (
        <p className="text-gray-500">No active search</p>
      )}
    </div>
  );

  const RecentMatchesCard = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Recent Matches</h3>
        <AlertTriangle className="h-5 w-5 text-orange-500" />
      </div>
      
      {stats.recentMatches.length > 0 ? (
        <div className="space-y-3">
          {stats.recentMatches.map((match, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {match.title || 'Unknown listing'}
                </p>
                <p className="text-xs text-gray-500">
                  Similarity: {(match.similarity * 100).toFixed(1)}%
                </p>
              </div>
              <div className="text-xs text-gray-400">
                {new Date(match.timestamp).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 text-center py-4">No matches found yet</p>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                  <div className="h-8 bg-gray-200 rounded w-16"></div>
                </div>
                <div className="h-12 w-12 bg-gray-200 rounded-full"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Dashboard
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Monitor your stolen image detection system. Track searches, view matches, and stay protected.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Stolen Images"
          value={stats.stolenImages}
          icon={Image}
          color="text-blue-600"
          subtitle="Images being monitored"
        />
        <StatCard
          title="Total Matches"
          value={stats.totalMatches}
          icon={AlertTriangle}
          color="text-red-600"
          subtitle="Potential matches found"
        />
        <StatCard
          title="Active Operation"
          value={searchStatus.is_running ? 1 : 0}
          icon={Search}
          color={searchStatus.is_running ? 'text-green-600' : 'text-gray-600'}
          subtitle={searchStatus.is_running ? 'In progress' : 'Idle'}
        />
        <StatCard
          title="System Status"
          value={stats.systemStatus === 'healthy' ? 'Healthy' : 'Error'}
          icon={stats.systemStatus === 'healthy' ? CheckCircle : XCircle}
          color={stats.systemStatus === 'healthy' ? 'text-green-600' : 'text-red-600'}
          subtitle="Server connection"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SearchStatusCard />
        <RecentMatchesCard />
      </div>

      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold mb-2">Ready to start monitoring?</h3>
            <p className="text-blue-100">
              Start a new search to find your stolen images on eBay.
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => window.location.href = '/search'}
              className="bg-white text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors"
            >
              Start Search
            </button>
            <button
              onClick={() => window.location.href = '/matches'}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors border border-blue-400"
            >
              View Matches
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

